import { useEffect } from 'react';

/** Un campo de captura tiene el foco: el atajo no debe robarselo. */
function escribiendo(destino: EventTarget | null): boolean {
  if (!(destino instanceof HTMLElement)) return false;
  if (destino.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(destino.tagName);
}

interface Opciones {
  /** Con Ctrl (o Cmd en Mac). */
  conControl?: boolean;
  /** Se dispara aunque el foco este en un campo. Para guardar, por ejemplo. */
  aunEscribiendo?: boolean;
  activo?: boolean;
}

/**
 * Atajo de teclado.
 *
 * El producto es una herramienta de captura: quien pasa veinte estudios al dia
 * deja de usar el raton para lo que repite. Los atajos se anuncian en la propia
 * interfaz con el componente `Tecla`, porque un atajo que no se ve no se
 * aprende.
 *
 * Nunca se secuestra una tecla suelta mientras alguien escribe. La excepcion es
 * explicita —`aunEscribiendo`— y hoy solo la usa guardar, que es justo lo que
 * se quiere poder hacer sin sacar las manos del formulario.
 */
export function useAtajo(
  tecla: string,
  alDisparar: () => void,
  { conControl = false, aunEscribiendo = false, activo = true }: Opciones = {},
): void {
  useEffect(() => {
    if (!activo) return;

    function alPulsar(evento: KeyboardEvent) {
      if (evento.key.toLowerCase() !== tecla.toLowerCase()) return;
      const control = evento.ctrlKey || evento.metaKey;
      if (conControl !== control) return;
      if (!aunEscribiendo && escribiendo(evento.target)) return;
      evento.preventDefault();
      alDisparar();
    }

    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [tecla, alDisparar, conControl, aunEscribiendo, activo]);
}
